
import React, { useState } from "react";
import { CalendarEvent } from "@/hooks/calendar/types";
import { AlertTriangle, Loader2 } from "lucide-react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface DeleteEventConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventToDelete: CalendarEvent | null;
  onConfirm: () => Promise<void>;
  onCancel: () => void;
}

export const DeleteEventConfirmDialog: React.FC<DeleteEventConfirmDialogProps> = ({
  open,
  onOpenChange,
  eventToDelete,
  onConfirm,
  onCancel
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Funzione per gestire la conferma di eliminazione
  const handleConfirm = async () => {
    if (!eventToDelete) return;
    
    try {
      setIsDeleting(true);
      setErrorMessage(null);
      console.log("DeleteEventConfirmDialog - Iniziata eliminazione dell'evento:", eventToDelete);
      
      await onConfirm();
      console.log("DeleteEventConfirmDialog - Completata eliminazione dell'evento");
    } catch (error) {
      console.error("DeleteEventConfirmDialog - Errore durante la conferma dell'eliminazione:", error);
      setErrorMessage(error instanceof Error ? error.message : "Errore durante l'eliminazione");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2">
              <p>Sei sicuro di voler eliminare questa sessione?</p>
              {eventToDelete?.type === "session" && (
                <div className="flex items-center text-amber-600 bg-amber-50 p-2 rounded-md text-sm">
                  <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>Le ore saranno restituite al pacchetto del cliente.</span>
                </div>
              )}
              {errorMessage && (
                <div className="flex items-center text-red-600 bg-red-50 p-2 rounded-md text-sm">
                  <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isDeleting}>Annulla</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm} 
            disabled={isDeleting}
            className="relative"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span>Eliminazione...</span>
              </>
            ) : (
              "Elimina"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
